/**
 * Packages Page Loader
 * Pre-loads initial packages data before rendering
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import { fetchActivePackagesAPI, type FetchPackagesResult } from '@/services/packagesService';
import type { PackageFilterParams } from '@/modules/packages/components/types';

export interface PackagesData {
  initialData: FetchPackagesResult;
  initialFilters: PackageFilterParams;
}

const DEFAULT_FILTERS: PackageFilterParams = {
  sort_by: 'sort_order',
  sort_order: 'asc',
  per_page: 20,
};

/**
 * Packages loader function
 */
export async function packagesLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    await requireAuth(args.request);

    const initialData = await fetchActivePackagesAPI(DEFAULT_FILTERS);

    const data: PackagesData = {
      initialData,
      initialFilters: DEFAULT_FILTERS,
    };

    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/packages', loadTime, dataSize);

    return data;
  } catch (error) {
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/packages', error as Error);
    throw error;
  }
}
